import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import type {
  MagicFlowOption,
  RuleSetRunRecord,
} from '../MagicFlowAdmin.types';

type RuleSetRunTableRecord = RuleSetRunRecord & {
  key: string;
};

type DebuggerTabProps = {
  hasOrgSelected: boolean;
  namespace: string;
  setNamespace: (value: string) => void;
  eventName: string;
  setEventName: (value: string) => void;
  eventId: string;
  setEventId: (value: string) => void;
  eventDataJson: string;
  setEventDataJson: (value: string) => void;
  namespaceOptions: MagicFlowOption[];
  isLoadingNamespaces: boolean;
  hasPublishErrors: boolean;
  isPublishingEvent: boolean;
  onPublishEvent: () => void;
  ruleSetRunsTableData: RuleSetRunTableRecord[];
  isLoadingRuleSetRuns: boolean;
  isFetchingRuleSetRuns: boolean;
  onRefreshRuleSetRuns: () => void;
};

const getRunEventName = (record: RuleSetRunTableRecord) => {
  const fromState =
    record.state &&
    typeof record.state === 'object' &&
    typeof (record.state as Record<string, unknown>).event_name === 'string'
      ? String((record.state as Record<string, unknown>).event_name)
      : '';

  if (fromState) return fromState;

  const maybeRules =
    record.rule_set &&
    typeof record.rule_set === 'object' &&
    Array.isArray((record.rule_set as Record<string, unknown>).rules)
      ? ((record.rule_set as Record<string, unknown>).rules as Array<
          Record<string, unknown>
        >)
      : [];

  const firstTrigger =
    maybeRules[0] &&
    typeof maybeRules[0] === 'object' &&
    maybeRules[0].trigger &&
    typeof maybeRules[0].trigger === 'object'
      ? (maybeRules[0].trigger as Record<string, unknown>)
      : null;

  return firstTrigger && typeof firstTrigger.event === 'string'
    ? String(firstTrigger.event)
    : '';
};

const getExecutionCount = (record: RuleSetRunTableRecord) => {
  const executions =
    record.state &&
    typeof record.state === 'object' &&
    Array.isArray((record.state as Record<string, unknown>).executions)
      ? ((record.state as Record<string, unknown>).executions as unknown[])
      : [];
  return executions.length;
};

const ruleSetRunColumns: ColumnsType<RuleSetRunTableRecord> = [
  {
    title: 'Run ID',
    dataIndex: 'rule_set_run_id',
    width: 280,
    render: (value) => (
      <Typography.Text className="font-mono">{value || '-'}</Typography.Text>
    ),
  },
  {
    title: 'Event ID',
    dataIndex: 'event_id',
    width: 260,
    render: (value) => (
      <Typography.Text className="font-mono">{value || '-'}</Typography.Text>
    ),
  },
  {
    title: 'Event Name',
    render: (_, record) => {
      const eventName = getRunEventName(record);
      return <Typography.Text>{eventName || '-'}</Typography.Text>;
    },
  },
  {
    title: 'Action',
    width: 180,
    render: (_, record) => {
      const executionCount = getExecutionCount(record);
      if (executionCount > 0) {
        return <Tag color="green">{`Triggered (${executionCount})`}</Tag>;
      }
      return <Tag color="default">No Action</Tag>;
    },
  },
  {
    title: 'Updated',
    dataIndex: 'updated_at',
    width: 220,
    render: (value) =>
      value ? dayjs(value).format('MMM D, YYYY h:mm:ss A') : '-',
  },
];

export const DebuggerTab = ({
  hasOrgSelected,
  namespace,
  setNamespace,
  eventName,
  setEventName,
  eventId,
  setEventId,
  eventDataJson,
  setEventDataJson,
  namespaceOptions,
  isLoadingNamespaces,
  hasPublishErrors,
  isPublishingEvent,
  onPublishEvent,
  ruleSetRunsTableData,
  isLoadingRuleSetRuns,
  isFetchingRuleSetRuns,
  onRefreshRuleSetRuns,
}: DebuggerTabProps) => {
  const [runEventNameFilter, setRunEventNameFilter] = useState<string>();
  const [rawRunModal, setRawRunModal] = useState<{
    open: boolean;
    run: RuleSetRunTableRecord | null;
  }>({
    open: false,
    run: null,
  });

  const eventNameFilterOptions = useMemo<MagicFlowOption[]>(
    () =>
      Array.from(
        new Set(
          ruleSetRunsTableData
            .map((run) => getRunEventName(run))
            .filter((name) => Boolean(name))
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    [ruleSetRunsTableData]
  );

  const filteredRuleSetRuns = useMemo(
    () =>
      runEventNameFilter
        ? ruleSetRunsTableData.filter(
            (run) => getRunEventName(run) === runEventNameFilter
          )
        : ruleSetRunsTableData,
    [ruleSetRunsTableData, runEventNameFilter]
  );

  const tableColumns = useMemo<ColumnsType<RuleSetRunTableRecord>>(
    () => [
      ...ruleSetRunColumns,
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, record) => (
          <Button
            size="small"
            onClick={() => setRawRunModal({ open: true, run: record })}
          >
            View Raw
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 p-4">
        <Typography.Title level={5} className="!mb-3">
          Publish Event
        </Typography.Title>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            placeholder="Select namespace"
            value={namespace || undefined}
            onChange={(value) => setNamespace(value)}
            options={namespaceOptions}
            loading={isLoadingNamespaces}
            disabled={!hasOrgSelected || isPublishingEvent}
            showSearch
            optionFilterProp="label"
          />
          <Input
            placeholder="event_name"
            value={eventName}
            onChange={(event) => setEventName(event.target.value)}
            disabled={!hasOrgSelected || isPublishingEvent}
          />
          <Input
            placeholder="event_id"
            value={eventId}
            onChange={(event) => setEventId(event.target.value)}
            disabled={!hasOrgSelected || isPublishingEvent}
          />
        </div>
        <Input.TextArea
          className="mt-3 font-mono"
          autoSize={{ minRows: 6, maxRows: 12 }}
          placeholder='{"key":"value"}'
          value={eventDataJson}
          onChange={(event) => setEventDataJson(event.target.value)}
          disabled={!hasOrgSelected || isPublishingEvent}
        />
        <div className="mt-3 flex justify-end">
          <Button
            type="primary"
            onClick={onPublishEvent}
            disabled={!hasOrgSelected || hasPublishErrors || isPublishingEvent}
            loading={isPublishingEvent}
          >
            Publish Event
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <Typography.Title level={5} className="!mb-0">
            Rule Set Runs
          </Typography.Title>
          <Space size={8}>
            <Select
              allowClear
              showSearch
              placeholder="Filter by event_name"
              optionFilterProp="label"
              value={runEventNameFilter}
              onChange={(value) => setRunEventNameFilter(value)}
              options={eventNameFilterOptions}
              disabled={!hasOrgSelected}
              className="min-w-[260px]"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={onRefreshRuleSetRuns}
              disabled={!hasOrgSelected || isLoadingRuleSetRuns}
              loading={isFetchingRuleSetRuns}
            >
              Refresh
            </Button>
          </Space>
        </div>
        <div className="ant-table rounded-xl shadow-xl">
          <Table
            className="appointment-page-table"
            dataSource={filteredRuleSetRuns}
            columns={tableColumns}
            rowKey="key"
            loading={isLoadingRuleSetRuns || isFetchingRuleSetRuns}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
            }}
            locale={{ emptyText: 'No Data' }}
          />
        </div>
      </div>
      <Modal
        open={rawRunModal.open}
        onCancel={() => setRawRunModal({ open: false, run: null })}
        footer={null}
        title="Rule Set Run Raw Data"
        width={900}
      >
        <pre className="max-h-[60vh] overflow-auto rounded bg-gray-50 p-3 text-xs">
          {JSON.stringify(rawRunModal.run, null, 2)}
        </pre>
      </Modal>
    </div>
  );
};
